import React from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage, getSrc } from 'gatsby-plugin-image';
import { Helmet } from 'react-helmet';
import { List } from '@pella/sash-framework';
import { isValidArray } from '../../utilities/validations';
import STLPinpoint from './stl-pinpoint';

const Image = ({ data, className, params = '', loading = 'lazy', objectFit = 'cover', disablePoints, onPointHover, activeHover, showTile, ...other }) => {
	if (!data?.image?.gatsbyImageData && !data?.image?.url) return null;

	const supportsWebP = () => {
		let elem = document.createElement('canvas');
		if (!!elem.getContext?.('2d')) {
			return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
		}
		return false;
	};

	let preloadSrc = null;
	let preloadSrcSet = null;
	let preloadSizes = null;
	if (loading === 'eager') {
		preloadSrc = getSrc(data.image);
		preloadSrcSet = supportsWebP ? data.image?.gatsbyImageData?.images?.sources?.[0]?.srcSet : data.image?.gatsbyImageData?.images?.fallback?.srcSet;
		preloadSizes = supportsWebP ? data.image?.gatsbyImageData?.images?.sources?.[0]?.sizes : data.image?.gatsbyImageData?.images?.fallback?.sizes;
	}

	return (
		<div className={`component-image relative inline-block ${className || ''}`}>
			{preloadSrc && (
				<Helmet>
					<link
						rel='preload'
						as='image'
						href={preloadSrc}
						imageSrcSet={preloadSrcSet}
						imageSizes={preloadSizes}
					/>
				</Helmet>
			)}

			{data.image.gatsbyImageData ? (
				<GatsbyImage
					image={data.image.gatsbyImageData}
					alt={data.image_alt || ''}
					title={data.image_title}
					className={className || null}
					loading={loading}
					objectFit={data.contain ? 'contain' : objectFit}
					fetchpriority={loading === 'eager' ? 'high' : 'low'}
					{...other}
				/>
			) : (
				<img
					src={`${data.image.url}${params}`}
					alt={data.image_alt || ''}
					title={data.image_title}
					className={className || null}
					loading={loading}
					decoding={loading === 'lazy' ? 'async' : 'auto'}
					fetchpriority={loading === 'eager' ? 'high' : 'low'}
					{...other}
				/>
			)}

			{!disablePoints && isValidArray(data.shop_the_look?.pinpoints) && (
				<List bullets='none'>
					{data.shop_the_look.pinpoints
						.filter((points) => isValidArray(points.tile) || isValidArray(points.tileConnection?.edges))
						.map((pinpoint, index) => {
							const tile = pinpoint.tile?.[0] || pinpoint.tileConnection?.edges?.[0]?.node;
							return (
								<li
									key={`pinpoint-${index}`}
									className='absolute z-2'
									style={{ top: pinpoint.position_vertical + '%', left: pinpoint.position_horizontal + '%' }}
								>
									<STLPinpoint
										tile={tile}
										onPointHover={onPointHover}
										activeHover={activeHover}
										showTile={showTile}
									/>
								</li>
							);
						})}
				</List>
			)}
		</div>
	);
};

Image.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
	params: PropTypes.string,
	loading: PropTypes.string,
	objectFit: PropTypes.string,
	disablePoints: PropTypes.bool,
	onPointHover: PropTypes.func,
	activeHover: PropTypes.string,
	showTile: PropTypes.bool,
};

export default Image;
